/* eslint-disable no-underscore-dangle */
/* eslint-disable react/jsx-no-useless-fragment */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { 
  Box, 
  CircularProgress, 
  Typography,
  Accordion as MuiAccordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MemoryIcon from '@mui/icons-material/Memory';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SecurityIcon from '@mui/icons-material/Security';
import InfoCard from '../../components/info-card';
import CardGrafica from '../../components/card-grafica';

const InfoTarjeta = ({ tarjeta }) => {
  const [tarjetaData, setTarjetaData] = useState(null);

  useEffect(() => {
    // Extraer los datos correctos considerando la estructura de Axios
    let datos = null;
    if (tarjeta && tarjeta.data && tarjeta.data) {
      datos = tarjeta.data;
    } else if (tarjeta && !tarjeta.data) {
      datos = tarjeta;
    }
    
    console.log('InfoTarjeta - Datos procesados:', datos); // Debug
    setTarjetaData(datos);
  }, [tarjeta]);

  if (!tarjeta) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="200px"
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Cargando información de la tarjeta...
        </Typography>
      </Box>
    );
  }

  if (!tarjetaData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No se pudo cargar la información de la tarjeta
        </Typography>
      </Box>
    );
  }

  // Preparar chips de estado
  const statusChips = [];
  if (tarjetaData.estatusDispositivo) {
    statusChips.push({ label: 'Activo', color: 'success' });
  } else {
    statusChips.push({ label: 'Inactivo', color: 'error' });
  }
  
  // Solo mostrar el chip de "Crítico" si alwaysOn es true
  if (tarjetaData.alwaysOn) {
    statusChips.push({ 
      label: 'Crítico', 
      color: 'warning',
      icon: <SecurityIcon fontSize="small" />
    });
  }

  // Datos básicos de la tarjeta
  const tarjetaBasicInfo = [
    { label: 'ID', value: tarjetaData._id },
    { label: 'Nombre', value: tarjetaData.nombre, highlight: true },
    { label: 'Proyecto', value: tarjetaData?.elemento?.proyecto?.nombre || 'No especificado' },
    { label: 'Elemento', value: tarjetaData?.elemento?.nombre || 'No especificado' },
    { label: 'Área', value: tarjetaData?.elemento?.area || 'No especificado' }
  ].filter(item => item.value !== 'No especificado'); // Filtrar campos vacíos

  return (
    <Box sx={{ p: 3 }}>
      {/* Información básica de la tarjeta */}
      <InfoCard
        title="Información de la Tarjeta"
        subtitle={`${tarjetaData?.elemento?.proyecto?.nombre || 'Proyecto no especificado'} - ${tarjetaData?.elemento?.nombre || 'Elemento no especificado'}`}
        icon={<MemoryIcon fontSize="large" />}
        items={tarjetaBasicInfo}
        chips={statusChips}
        variant="elevation"
      />
      
      {/* Acordeón de gráficas */}
      <MuiAccordion defaultExpanded sx={{ mt: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
          sx={{ 
            backgroundColor: '#1F2A38', 
            color: 'white',
            '&:hover': {
              backgroundColor: '#303C54',
            },
            '& .MuiTypography-root': {
              color: 'white',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShowChartIcon />
            <Typography variant="h6">
              Gráficas {tarjetaData?.graficas ? `(${tarjetaData.graficas.length})` : '(0)'}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2 }}>
          {tarjetaData?.graficas && tarjetaData.graficas.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {tarjetaData.graficas.map((item, index) => (
                <Box key={item._id || index}>
                  <CardGrafica
                    idGrafica={item._id}
                    title={item.nombre}
                    data={item.sensores}
                  />
                  {index < tarjetaData.graficas.length - 1 && (
                    <Divider sx={{ mt: 2 }} />
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <InfoCard
              title="Sin gráficas configuradas"
              subtitle="No hay gráficas asociadas a esta tarjeta"
              icon={<ShowChartIcon />}
              items={[]}
              chips={[{ label: 'Sin configurar', color: 'default', variant: 'outlined' }]}
            />
          )}
        </AccordionDetails>
      </MuiAccordion>
    </Box>
  );
};

InfoTarjeta.propTypes = {
  tarjeta: PropTypes.oneOfType([
    PropTypes.shape({
      data: PropTypes.shape({
        _id: PropTypes.string,
        nombre: PropTypes.string,
        posicionesEnTrama: PropTypes.number,
        estatusDispositivo: PropTypes.bool,
        alwaysOn: PropTypes.bool,
        elemento: PropTypes.shape({
          nombre: PropTypes.string,
          area: PropTypes.string,
          proyecto: PropTypes.shape({
            nombre: PropTypes.string,
          }),
        }),
        graficas: PropTypes.array,
      })
    }),
    PropTypes.shape({
      _id: PropTypes.string,
      nombre: PropTypes.string,
      posicionesEnTrama: PropTypes.number,
      estatusDispositivo: PropTypes.bool,
      alwaysOn: PropTypes.bool,
      elemento: PropTypes.shape({
        nombre: PropTypes.string,
        area: PropTypes.string,
        proyecto: PropTypes.shape({
          nombre: PropTypes.string,
        }),
      }),
      graficas: PropTypes.array,
    })
  ]),
};

InfoTarjeta.defaultProps = {
  tarjeta: null,
};

export default InfoTarjeta;
